import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuList,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import type { AppBarProps, SxProps } from '@mui/material';
import { NavLink, useLocation } from '@remix-run/react';

import {
  Checkmark,
  ChevronRight,
  Menu,
  SwitchApp,
} from '@tbd/life-jsx/SystemIcons';
import { BOXSHADOWS, COLORS } from '@tbd/life-tokens';
import {
  AppTitles,
  getOrigins,
  isElevatedRoleName,
  ROLE_NAMES,
} from '@tbd/lift-portal-shared-config';
import type { AppName } from '@tbd/lift-portal-shared-config';
import { LogoTitleLockup } from '@tbd/lift-ui/components/LogoTitleLockup';
import { ProfileMenu } from '@tbd/lift-ui/components/ProfileMenu';
import type { ProfileMenuProps } from '@tbd/lift-ui/components/ProfileMenu';
import type { LiftTypographyVariants } from '@tbd/lift-ui/mui';

export const BAR_HEIGHT = '64px';
const BACKDROP_OVERLAY_WIDTH = '100vw';

export type NavLinksDefinitions = Array<{ path: string; label: string }>;

export interface GlobalNavigationBarProps {
  sx?: AppBarProps['sx'];
  navLinks?: NavLinksDefinitions /* Navigation links provided by each app */;
  hostname: string;
  appName: AppName;
  logoTitleLockupLink?: string;
  user?: ProfileMenuProps['user'];
  groupSelectionHref?: ProfileMenuProps['groupSelectionHref'];
  projectSelectionHref?: ProfileMenuProps['projectSelectionHref'];
  currentProjectName?: ProfileMenuProps['currentProjectName'];
}

const DEFAULT_SX = {};

function MenuNavLink({
  path,
  label,
  isActive = false,
  includeOffPathIcon = false,
  typographyVariant,
  sx = DEFAULT_SX,
}: {
  path: string;
  label: string;
  typographyVariant: keyof LiftTypographyVariants;
  isActive?: boolean;
  includeOffPathIcon?: boolean;
  sx?: SxProps;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      <Button
        fullWidth
        component={NavLink}
        to={path}
        sx={{
          padding: theme.spacing(1),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          '&:hover': {
            backgroundColor: COLORS.accent1_50,
          },
          '&:focus': {
            backgroundColor: COLORS.accent1_50,
          },
          ...sx,
        }}
      >
        <ListItem sx={{ paddingX: 'unset', paddingY: 'unset' }}>
          <ListItemText sx={{ margin: 'unset', padding: 'unset' }}>
            <Typography color={COLORS.black} variant={typographyVariant}>
              {label}
            </Typography>
          </ListItemText>

          <ListItemIcon
            style={{
              minWidth: '24px',
            }}
          >
            {isActive ? (
              <Checkmark sx={{ color: COLORS.accent1_500 }} />
            ) : includeOffPathIcon ? (
              <ChevronRight
                sx={{
                  color: COLORS.black,
                }}
              />
            ) : null}
          </ListItemIcon>
        </ListItem>
      </Button>
    </Box>
  );
}

function NavLinkDrawer({ navLinks }: { navLinks: NavLinksDefinitions }) {
  const theme = useTheme();
  const location = useLocation();
  return (
    <Box
      sx={{
        display: { xs: 'flex', lg: 'none' },
        paddingX: 1,
      }}
    >
      <Box
        component="details"
        name="mutually-exclusive-navigation-menu"
        key={location.key}
        sx={{
          '&[open] > summary::before': {
            content: "' '",
            display: 'block',
            position: 'fixed',
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
          },
          // Define the Menu Icon states
          '&[open] > summary > .close-icon': {
            display: 'block',
          },
          '&[open] > summary': {
            display: 'block',
            backgroundColor: COLORS.accent1_100,
            border: 0,
            borderRadius: '4px',
          },
          '&[open] > summary > .menu-icon': {
            display: 'none',
          },
          '& > summary > .close-icon': {
            display: 'none',
          },
        }}
      >
        <IconButton
          component="summary"
          size="medium"
          aria-label="Navigation menu"
          aria-controls="menu-navigation"
          aria-haspopup="true"
          sx={{
            border: 0,
            '&::before': { display: 'none' },
            '&::-webkit-details-marker': { display: 'none' },
          }}
        >
          <Menu className="menu-icon" />
          <Menu className="close-icon" />
        </IconButton>
        <Stack
          component="nav"
          sx={{
            flexGrow: 1,
            position: 'absolute',
            paddingX: {
              xs: 2,
              sm: 4,
            },
            top: BAR_HEIGHT,
            left: '0px',
            width: {
              xs: `calc(100vw - (${theme.spacing(2)}) * 2)`, // full width - the paddingX
              sm: `calc(100vw - (${theme.spacing(4)}) * 2)`, // full width - the paddingX
            },
            boxShadow: 'unset',
            backgroundColor: COLORS.white,
            /* The pseudo-element injected to create the backdrop */
            '&::before': {
              content: '""',
              position: 'fixed',
              top: BAR_HEIGHT,
              left: 0,
              width: BACKDROP_OVERLAY_WIDTH,
              height: `calc(100% - ${BAR_HEIGHT})`,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: -1,
            },
          }}
        >
          <MenuList sx={{ paddingY: 2 }}>
            <Stack
              sx={{
                gap: 1,
              }}
            >
              <Typography
                variant="component-regular-s"
                component="h1"
                noWrap
                sx={{ color: COLORS.neutral_600, paddingX: 1 }}
              >
                Menu
              </Typography>
              {navLinks.map(({ label, path }) => (
                <MenuNavLink
                  isActive={location.pathname === path}
                  key={path}
                  path={path}
                  label={label}
                  typographyVariant="component-regular-m"
                  includeOffPathIcon
                  sx={{ paddingY: '12px' }}
                />
              ))}
            </Stack>
          </MenuList>
        </Stack>
      </Box>
    </Box>
  );
}

function GlobalNavBarButtonLink(props: { path: string; label: string }) {
  return (
    <Button
      component={NavLink}
      to={props.path}
      sx={{
        display: 'block',
        height: BAR_HEIGHT,
        lineHeight: '32px',
        margin: 0,
        padding: 2,
        borderRadius: 0,
        borderBottom: '1px solid transparent',
        '&:hover': {
          backgroundColor: COLORS.accent1_50,
          borderBottom: `1px solid ${COLORS.neutral_200}`,
        },
        '&:focus': {
          backgroundColor: COLORS.accent1_50,
        },
        '&:active, &.active': {
          backgroundColor: COLORS.accent1_100,
          borderBottom: `4px solid ${COLORS.accent1_600}`,
          '& .MuiTypography-root': {
            fontWeight: 700,
            color: COLORS.accent1_600,
          },
        },
      }}
    >
      <Typography sx={{ color: COLORS.neutral_500 }} variant="paragraph-s">
        {props.label}
      </Typography>
    </Button>
  );
}

export const GLOBAL_NAV_PADDING_X = {
  xs: 1,
  sm: 3,
  lg: 4,
  xxl: 8,
};

export const GlobalNavigationBar = ({
  sx,
  logoTitleLockupLink = '/',
  navLinks,
  appName,
  user,
  hostname,
  groupSelectionHref,
  projectSelectionHref,
  currentProjectName,
}: GlobalNavigationBarProps) => {
  const showElevatedAdminComponents = user && isElevatedRoleName(user.role);
  const ORIGINS = getOrigins({ hostname });
  return (
    <AppBar
      position="sticky"
      sx={{
        height: BAR_HEIGHT,
        top: 0,
        backgroundColor: COLORS.white,
        borderBottom: `1px solid ${COLORS.neutral_200}`,
        boxShadow: 'none',
        ...(sx || {}),
      }}
    >
      <Container maxWidth={false} disableGutters>
        <Toolbar
          disableGutters
          sx={{
            justifyContent: 'space-between',
            height: BAR_HEIGHT,
            paddingX: GLOBAL_NAV_PADDING_X,
          }}
        >
          <Stack
            direction="row"
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            {showElevatedAdminComponents ? (
              <AppSwitcher
                activeAppName={appName}
                hostname={hostname}
                user={user}
              />
            ) : null}
            <NavLink
              to={logoTitleLockupLink}
              style={() => {
                return {
                  textDecoration: 'none',
                };
              }}
            >
              <LogoTitleLockup
                appName={appName}
                textColor="black"
                logoVariant="multi-colored"
              />
            </NavLink>
            <Stack
              direction="row"
              sx={{
                paddingX: '28px',
                display: { xs: 'none', lg: 'flex' },
              }}
            >
              {navLinks?.length
                ? navLinks.map(({ path, label }) => (
                    <GlobalNavBarButtonLink
                      key={path}
                      path={path}
                      label={label}
                    />
                  ))
                : null}
            </Stack>
          </Stack>
          <Stack
            direction="row"
            sx={{
              gap: 1,
            }}
          >
            <Stack
              sx={{
                alignContent: 'center',
                justifyContent: 'center',
              }}
            >
              {user ? (
                <ProfileMenu
                  user={user}
                  portalOrigin={ORIGINS.portal}
                  groupSelectionHref={groupSelectionHref}
                  projectSelectionHref={projectSelectionHref}
                  currentProjectName={currentProjectName}
                />
              ) : null}
            </Stack>
            {navLinks?.length ? <NavLinkDrawer navLinks={navLinks} /> : null}
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
};
type AppLinkDefinition = {
  appName: AppName;
  path: string;
  label: string;
};
function AppSwitcher({
  user,
  hostname,
  activeAppName,
}: {
  user: ProfileMenuProps['user'];
  hostname: string;
  activeAppName: AppName;
}) {
  const appSwitcherId = 'app-switcher-menu';
  const theme = useTheme();
  const location = useLocation();
  const ORIGINS = getOrigins({ hostname });
  const appLinks: AppLinkDefinition[] = [
    {
      appName: 'assessments',
      path: ORIGINS.assessments,
      label: AppTitles.assessments,
    },
    {
      appName: 'practice',
      path: ORIGINS.practice,
      label: AppTitles.practice,
    },
  ];

  return (
    <Box
      component="details"
      name="mutually-exclusive-navigation-menu"
      // use the location key to force the menu to re-render (close) when the user navigates to a new page
      key={location.key}
      sx={{
        '&[open] > summary::before': {
          content: "' '",
          display: 'block',
          position: 'fixed',
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
        },
        // Define the Menu Icon states styling
        '&[open] > summary > .app-switcher-close-icon': {
          display: 'block',
        },
        '&[open] > summary': {
          display: 'block',
          backgroundColor: COLORS.accent1_100,
          border: 0,
          borderRadius: '4px',
        },
        '&[open] > summary > .app-switcher-menu-icon': {
          display: 'none',
        },
        '& > summary > .app-switcher-close-icon': {
          display: 'none',
        },
        // end menu icon states styling
      }}
    >
      <IconButton
        component="summary"
        size="medium"
        aria-label="Navigation menu"
        aria-controls={appSwitcherId}
        aria-haspopup="true"
        sx={{
          border: 0,
          /* Remove default details arrow */
          '&::-webkit-details-marker': { display: 'none' },
          '&::before': { display: 'none' },
        }}
      >
        <SwitchApp className="app-switcher-menu-icon" />
        <SwitchApp className="app-switcher-close-icon" />
      </IconButton>
      <Stack
        component="nav"
        sx={{
          /* Common styling between fixed drawer menu and floating small menu */
          position: 'absolute',
          backgroundColor: COLORS.white,

          /* Fixed drawer (stretched menu) styling */
          [theme.breakpoints.down('lg')]: {
            paddingX: {
              xs: 2,
              sm: 4,
            },
            paddingY: 2,
            top: BAR_HEIGHT,
            left: '0px',
            width: {
              xs: `calc(100vw - (${theme.spacing(2)}) * 2)`, // full width - the paddingX
              sm: `calc(100vw - (${theme.spacing(4)}) * 2)`, // full width - the paddingX
            },
            boxShadow: 'unset',
            /* The pseudo-element injected to create the backdrop */
            '&::before': {
              content: '""',
              position: 'fixed',
              top: BAR_HEIGHT,
              left: 0,
              width: BACKDROP_OVERLAY_WIDTH,
              height: `calc(100% - ${BAR_HEIGHT})`,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: -1,
            },
          },

          /* Use floating small menu on desktop screens */
          [theme.breakpoints.up('lg')]: {
            '--icon-top-margin': '12px',
            '--icon-size': '40px',
            '--menu-offset': '4px',
            top: `calc(var(--icon-top-margin) + var(--icon-size) + var(--menu-offset))`,
            width: '342px',
            height: 'auto',
            left: {
              lg: theme.spacing(GLOBAL_NAV_PADDING_X.lg),
              xxl: theme.spacing(GLOBAL_NAV_PADDING_X.xxl),
            },
            boxSizing: 'border-box',
            zIndex: '2',
            borderRadius: '6px',
            listStyle: 'none',
            boxShadow: BOXSHADOWS.BOXSHADOW_CENTERED,
          },
        }}
      >
        <MenuList
          component="ul"
          id={appSwitcherId}
          sx={{
            padding: {
              xs: '0',
              lg: '0 0 12px 0',
            },
          }}
        >
          <Box
            sx={{
              padding: {
                xs: theme.spacing(0, 1, 2, 1),
                lg: theme.spacing(2),
              },
            }}
          >
            <Typography
              variant="component-regular-m"
              component="h3"
              noWrap
              sx={{ color: COLORS.neutral_800 }}
            >
              {user.firstName} {user.lastName}
            </Typography>
            <Typography
              variant="component-regular-xs"
              component="h5"
              noWrap
              sx={{
                display:
                  user.role === ROLE_NAMES.PARTICIPANT ? 'none' : 'block',
                paddingY: '2px',
                color: COLORS.neutral_700,
              }}
            >
              {user.email}
            </Typography>
            <Typography
              variant="component-regular-xxs"
              component="h6"
              noWrap
              sx={{ paddingY: '2px', color: COLORS.neutral_700 }}
            >
              {user.role}
            </Typography>
          </Box>
          <Divider />

          <Box
            sx={{
              padding: {
                xs: theme.spacing(1, 0),
                lg: theme.spacing(1),
              },
            }}
          >
            <MenuNavLink
              path={ORIGINS.portal}
              isActive={activeAppName === 'portal'}
              label={AppTitles.portal}
              typographyVariant="component-regular-s"
            />
          </Box>

          <Divider />
          <Stack
            sx={{
              gap: {
                xs: 1,
                lg: 0,
              },
              padding: {
                xs: theme.spacing(1, 0, 0, 0),
                lg: theme.spacing(0, 1),
              },
            }}
          >
            <Typography
              variant="component-bold-xxs"
              component="h6"
              sx={{
                textTransform: 'uppercase',
                color: COLORS.neutral_700,

                padding: {
                  xs: '4px 8px',
                  lg: '12px 8px 8px 8px',
                },
              }}
            >
              Your Apps
            </Typography>
            {appLinks
              ? appLinks.map(({ label, path, appName }) => (
                  <Box
                    key={path}
                    sx={{
                      paddingY: { xs: 0, lg: '2px' },
                    }}
                  >
                    <MenuNavLink
                      isActive={activeAppName === appName}
                      path={path}
                      label={label}
                      typographyVariant="component-regular-s"
                    />
                  </Box>
                ))
              : null}
          </Stack>
        </MenuList>
      </Stack>
    </Box>
  );
}
